import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import {
  FEED_POST_BUILD_SOLR_QUERY_FROM_FILTERS,
  FEED_POST_COPY_FEED,
  FEED_PUT_FEED,
  FEED_PATCH_FEED,
  FEED_POST_EXPORT,
  FEED_POST_FILTER,
  FEED_POST_RESULTS,
  FEED_GET_REMOVED_ITEMS,
  FEED_GET_CATEGORIES,
  FEED_PATCH_REMOVED_ITEMS,
  FEED_POST_REMOVE_SELECTED_ITEMS,
  FEED_DELETE_REMOVED_ITEMS,
  FEED_PATCH_VIEWS,
  FEED_PATCH_CONTEXT_MENU_OPTIONS,
  FEED_PATCH_POST_VIEW_MODE,
  FEED_POST_LIVE_FEED_STATUS,
  FEED_POST_VALIDATE_USER_QUERY,
  FEED_GET_FILTERS_LIST,
  FEED_GET_RESOLVE_FILTER_ITEM,
  FEED_GET_APPLICABLE_CHARTS,
  FEED_GET_OPERATOR_HINTS,
  FEED_GET_ALGORITHMS,
  FEED_DELETE_ALGORITHM,
  FEED_PATCH_ALGORITHM_PARAMS,
  FEED_PATCH_PROCESS,
  FEED_GET_CONTEXTS,
  FEED_POST_LIVE_PREVIEW,
} from '../endpoints/feed.endpoints';

import { toFormBody, toQueryParams } from '../api-utils';
import { contentFiltersFormData } from '../serialize/feed.serialize';
import { HEADERS } from '../api-headers';

import {
  ContentFilters,
  CopyFeedResponse,
  Feed,
  FeedRemovedItem,
  Filter,
  FilterItem,
  Post,
  QueryBuilderContext,
} from 'src/app/core/models/core.models';
import { NomObjectClass } from 'src/app/core/constants';
import {
  AttachFeedAlgorithmsResponse,
  FeedAlgorithm,
} from 'src/app/core/models/algorithm.models';
import { WidgetPreset } from 'src/app/core/models/widget.models';
import {
  FeedOperatorHint,
  LivePreviewResponse,
} from 'src/app/core/services/feed.service';

import { TimeShown } from 'src/app/core/reducers/feed.reducer';

import { Observable } from 'rxjs';

export interface AutoStoryPrompt {
  id: number;
  vendor: string;
  model_name: string;
  role: string;
  name: string;
  template: string;
  prompt: string;
  _meta_info: {
    db_doc: number;
  };
}

export interface PromptParameterValidator {
  type: string;
  args: {
    min?: number;
    max?: number;
    [key: string]: any;
  };
  errormsg: string;
}

export interface PromptParameterOption {
  id: number;
  name: string;
}

export interface PromptParameter {
  type: string;
  default?: any;
  value?: any;
  value_column?: string;
  validators: PromptParameterValidator[];
  attributes: {
    min?: number;
    max?: number;
    [key: string]: any;
  };
  options?: PromptParameterOption[];
}

export interface PromptParametersSchema {
  [key: string]: PromptParameter;
}

export interface FiltersListResponse {
  available: Array<Filter>;
  saved: Array<Filter>;
}

export type FeedPostViewMode = 'default' | 'threads' | 'compact' | 'gallery';

export type FeedAllowedViewsValues = 'list' | 'analytics' | 'details';

export type FeedAllowedViewsValuesArray = FeedAllowedViewsValues[];

export interface WidgetViewProperties {
  widget_display_attached_pivots?: boolean;
  widget_display_attached_filters?: boolean;
  widget_display_attached_data_sources?: boolean;
  post_sentiment_algorithm_id?: number[] | null;
  show_advanced_quick_search?: boolean;
  quick_search_query?: string;
}

export interface FeedSettings {
  sort_direction: string;
  order_field: string;
  post_time_mode: 'relative' | 'absolute';
  post_time_field: 'post_time_published' | 'post_time_created';
  items_per_page: number;
  post_display_thumbnail: boolean;
  view_properties: WidgetViewProperties;
  time_shown: TimeShown;
}

export interface CopyFeedParams {
  workspace_id: string;
  title?: string;
  prefix?: string;
  description: string;
  query_type_id: number;
  object_ids: string[];
  duplicateWidgets: boolean;
  duplicateAlerts: boolean;
  duplicateExportSchedules: boolean;
  duplicateOutputSettings: boolean;
  duplicateContentFilters: boolean;
  duplicateRemovedItems: boolean;
  preserveLinkedDimensions: boolean;
  duplicatePermissions: boolean;
}

export type QueryValidationResponse =
  | QueryValidationSuccess
  | QueryValidationError;

export interface QueryValidationSuccess {
  success: true;
}

export interface QueryValidationError {
  error: true;
  errorCode: number;
  line: number;
  column: number;
  charPos: number;
  message: string;
}

interface NotificationPushService {
  id: number;
  value: boolean;
}

export interface AlertData {
  title: string;
  use_alert_title_as_subject: boolean;
  deviation_threshold_value: number;
  deviation_period: number;
  deviation_direction: number;
  privacy_id: boolean;
  description: string;
  trigger_type_id: number;
  notification_posts_count: number;
  trigger_new_posts_count: number;
  notification_post_order_type_id: number;
  filters?: any; // Assuming filters is a string, but it might be more complex.
  notification_push_service?: NotificationPushService[];
}

export interface PostsResponse {
  posts: Array<Post>;
  count_posts: number;
}

export interface FilterItemResponse {
  has_more: boolean;
  items: Array<FilterItem>;
  property_uri: string;
  offset: number;
  limit: number;
}

export interface FilterItemResolveResponse {
  property_title: string;
  property_value: any;
  property_value_title: string | null;
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({
  providedIn: 'root',
})
export class FeedApiService {
  private readonly http = inject(HttpClient);

  getUserCategories(feedId: string): Observable<any> {
    return this.http.get<Array<any>>(FEED_GET_CATEGORIES(feedId));
  }

  getFeedFilters(objectId: string): Observable<FiltersListResponse> {
    return this.http.get<FiltersListResponse>(FEED_GET_FILTERS_LIST(objectId));
  }

  /**
   * Query-language operator hints for an object type — reference data for the
   * query editor's autocomplete and hints popup.
   */
  getFeedOperatorHints(objectTypeId: number): Observable<FeedOperatorHint[]> {
    const params = toQueryParams({ object_type_id: objectTypeId });

    return this.http.get<FeedOperatorHint[]>(FEED_GET_OPERATOR_HINTS, {
      params,
    });
  }

  /**
   * Widget presets applicable to an object type/class — the widget gallery's
   * catalogue. URL root is `/feed`, hence this service.
   */
  getApplicableCharts(
    objectTypeId: number,
    objectClassId: number,
  ): Observable<Array<WidgetPreset>> {
    const params = toQueryParams({
      object_type_id: objectTypeId,
      object_class_id: objectClassId,
    });

    return this.http.get<Array<WidgetPreset>>(FEED_GET_APPLICABLE_CHARTS, {
      params,
    });
  }

  // ! ─── Feed view state ────────────────────────────────────────────────────
  // ! Bodies are `URLSearchParams` with a `Content-Type` that carries no
  // ! charset. Both are what the backend expects; don't normalise them.

  /** Persist which panes (`list` / `analytics` / `details`) a feed shows. */
  saveFeedViewsState(
    feedId: string,
    views: FeedAllowedViewsValuesArray,
  ): Observable<unknown> {
    const body = new URLSearchParams();
    for (const view of views) {
      body.append('views[]', view.toString());
    }

    return this.http.patch(FEED_PATCH_VIEWS(feedId), body.toString(), {
      headers: HEADERS.formUrlEncodedNoCharset,
    });
  }

  /** Persist the post-list layout (`default` / `threads` / `compact` / `gallery`). */
  setFeedPostViewMode(
    feedId: string,
    viewMode: FeedPostViewMode,
  ): Observable<unknown> {
    const body = new URLSearchParams();
    body.append('viewMode', viewMode.toString());

    return this.http.patch(FEED_PATCH_POST_VIEW_MODE(feedId), body.toString(), {
      headers: HEADERS.formUrlEncodedNoCharset,
    });
  }

  saveFeedSettings(feedId: string, data: FeedSettings) {
    const headers = HEADERS.formUrlEncoded;
    const httpParams = toFormBody(data);

    return this.http.patch(
      FEED_PATCH_CONTEXT_MENU_OPTIONS(feedId),
      httpParams,
      { headers },
    );
  }

  /**
   * Query contexts, used in the search builder.
   */
  getFeedQueryContexts(feedId: string): Observable<Array<QueryBuilderContext>> {
    return this.http.get<Array<QueryBuilderContext>>(FEED_GET_CONTEXTS(feedId));
  }

  fetchLiveData(
    query: any,
    objectTypeId: number,
    combinedFeedIds: string[] = [],
    contentPlatformIds: number[] = [],
  ): Observable<LivePreviewResponse> {
    const headers = HEADERS.formUrlEncoded;

    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('query', query);
    urlEncodedData.append('object_type_id', objectTypeId.toString());

    combinedFeedIds.forEach((id) => {
      urlEncodedData.append('combined_feed_ids[]', id);
    });

    contentPlatformIds.forEach((id) => {
      urlEncodedData.append('content_platform_ids[]', id.toString());
    });

    return this.http.post<LivePreviewResponse>(
      FEED_POST_LIVE_PREVIEW,
      urlEncodedData.toString(),
      { headers },
    );
  }

  getSolrQueryFromContentFilters(
    contentFilters: ContentFilters,
  ): Observable<string> {
    return this.http.post<string>(
      FEED_POST_BUILD_SOLR_QUERY_FROM_FILTERS,
      contentFilters,
    );
  }

  duplicateFeeds(copyParams: CopyFeedParams): Observable<CopyFeedResponse> {
    return this.http.post<CopyFeedResponse>(FEED_POST_COPY_FEED, copyParams);
  }

  // ! ─── Feed create / edit / query validation ───────────────────────────────

  createFeed(feedData: any): Observable<Feed> {
    return this.http.put<Feed>(FEED_PUT_FEED, toFormBody(feedData), {
      headers: HEADERS.formUrlEncoded,
    });
  }

  editFeed(feedId: string, feedData: any): Observable<Feed> {
    return this.http.patch<Feed>(
      FEED_PATCH_FEED(feedId),
      toFormBody(feedData),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  /**
   * Check a query before saving it. Falsy values are **skipped**, not sent as
   * empty strings - load-bearing for `query_id`, which is absent on a create.
   */
  validateQuery(
    objectTypeId: number,
    query: string,
    queryCreatedFrom: 'editor' | 'simple',
    queryId?: string,
  ): Observable<QueryValidationResponse> {
    const params = {
      query_id: queryId,
      object_type_id: objectTypeId,
      query,
      query_created_from: queryCreatedFrom,
    };

    const body = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key]) {
        body.append(key, params[key].toString());
      }
    });

    return this.http.post<QueryValidationResponse>(
      FEED_POST_VALIDATE_USER_QUERY,
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  // ! ─── Feed algorithms ────────────────────────────────────────────────────

  getFeedAlgorithms(feedId: string): Observable<FeedAlgorithm[]> {
    return this.http.get<FeedAlgorithm[]>(FEED_GET_ALGORITHMS(feedId));
  }

  // ! There is no attach request: an algorithm is attached by **scheduling** it,
  // ! which `applyFeedPostsAlgorithm` below does server-side (WAP-1379). The old
  // ! `PATCH /feed/{id}/algorithms` is being deprecated backend-side.

  /**
   * `scheduleId` addresses one specific schedule row: the same algorithm can be
   * attached to a feed by several users, and the row keeps its original owner
   * when someone else edits it (SEN-5120).
   */
  detachFeedAlgorithm(
    feedId: string,
    algorithmId: number,
    scheduleId?: string | null,
  ): Observable<AttachFeedAlgorithmsResponse> {
    return this.http.delete<AttachFeedAlgorithmsResponse>(
      FEED_DELETE_ALGORITHM(feedId),
      {
        params: toQueryParams({
          algorithm_id: algorithmId,
          schedule_id: scheduleId,
        }),
      },
    );
  }

  updateFeedAlgorithmParams(
    feedId: string,
    algorithmId: number,
    params: Record<string, any>,
    scheduleId?: string | null,
  ): Observable<AttachFeedAlgorithmsResponse> {
    return this.http.patch<AttachFeedAlgorithmsResponse>(
      FEED_PATCH_ALGORITHM_PARAMS(feedId),
      // ! `toFormBody` copies keys verbatim and turns null into '', so the
      // ! wire names are spelled out here and `schedule_id` is spread in only
      // ! when it has a value.
      toFormBody({
        algorithm_id: algorithmId,
        params,
        ...(scheduleId != null ? { schedule_id: scheduleId } : {}),
      }),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  /** Run an algorithm over a feed's result set - answers with the notification id tracking the run. */
  applyFeedPostsAlgorithm(
    feedId: string,
    algorithmId: number,
    contentFilters: ContentFilters,
    algorithmParams: Record<string, any> = {},
  ): Observable<string> {
    return this.http.patch<string>(FEED_PATCH_PROCESS, {
      query_id: feedId,
      algorithm_id: algorithmId,
      params: algorithmParams,
      content_filters: contentFilters,
    });
  }

  /**
   * The feed's result set. `force_unlock_period` is what the post-related
   * dialog's search sends to ignore the feed's period lock.
   */
  getFeedPosts(
    feedId: string,
    contentFilters: ContentFilters,
    forceUnlockPeriod: boolean = false,
  ): Observable<PostsResponse> {
    const formData = contentFiltersFormData(contentFilters);
    formData.append('force_unlock_period', forceUnlockPeriod.toString());

    return this.http.post<PostsResponse>(FEED_POST_RESULTS(feedId), formData);
  }

  /**
   * One page of a single filter's items, narrowed by the feed's active content
   * filters - which is why the panel re-reads every expanded filter whenever
   * those change. `offset > 0` is the "show more" page.
   */
  getFeedFilterItems(
    feedId: string,
    filter: Filter,
    contentFilters: ContentFilters,
    offset: number = 0,
  ): Observable<FilterItemResponse> {
    const formData = contentFiltersFormData(contentFilters);
    formData.append('property_uri', filter.property_uri);
    formData.append('property_uri_facet', filter.property_uri_facet);
    formData.append('offset', offset.toString());

    return this.http.post<FilterItemResponse>(
      FEED_POST_FILTER(feedId),
      formData,
    );
  }

  /**
   * Titles for a filter item that is not in any loaded filter - what a widget
   * point-click produces, since it names a value the panel may never have shown.
   */
  getValueFilterItemMeta(
    propertyUri: string,
    rawValue: string,
  ): Observable<FilterItemResolveResponse> {
    return this.http.get<FilterItemResolveResponse>(
      FEED_GET_RESOLVE_FILTER_ITEM,
      { params: { property_uri: propertyUri, value: rawValue } },
    );
  }

  exportFeed(feedId: string, payload: any): Observable<any> {
    return this.http.post(FEED_POST_EXPORT(feedId), payload);
  }

  toggleLiveFeedStatus(feed: any) {
    return this.http.post(FEED_POST_LIVE_FEED_STATUS(feed?.id), {});
  }

  // ! ─── Removed ("excluded") items ─────────────────────────────────────────
  // ! Bodies are `URLSearchParams`, **not** `toQueryParams`: these carry post and
  // ! source ids, and Angular's `standardEncoding` leaves `=`, `;` and `,` raw,
  // ! which corrupts the next field of a form body. The `Content-Type` also
  // ! differs per request (charset vs. no charset); both are what the backend
  // ! expects, so don't normalise them.

  getRemovedItems(feedId: string): Observable<FeedRemovedItem[]> {
    return this.http.get<FeedRemovedItem[]>(FEED_GET_REMOVED_ITEMS(feedId));
  }

  /** Remove one post from a feed; answers with the items it removed. */
  removePostFromFeed(
    feedId: string,
    postId: string,
  ): Observable<FeedRemovedItem[]> {
    const body = new URLSearchParams();
    body.append('object_ids[]', postId);
    body.set('object_class_id', NomObjectClass.POST.toString());

    return this.http.patch<FeedRemovedItem[]>(
      FEED_PATCH_REMOVED_ITEMS(feedId),
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  /**
   * Remove every post of one or more sources — "remove all posts from this
   * source". `apply_to_child_objects` is always `true`: that *is* the feature.
   */
  removeItemsBySource(
    feedId: string,
    objectIds: string[],
    objectClassId: number,
  ): Observable<FeedRemovedItem[]> {
    const body = new URLSearchParams();
    objectIds.forEach((id) => body.append('object_ids[]', id));
    body.set('object_class_id', objectClassId.toString());
    body.set('apply_to_child_objects', 'true');

    return this.http.patch<FeedRemovedItem[]>(
      FEED_PATCH_REMOVED_ITEMS(feedId),
      body.toString(),
      { headers: HEADERS.formUrlEncodedNoCharset },
    );
  }

  /** Remove the feed's whole selection; answers with the full removed list. */
  removeSelectedPostsFromFeed(
    feedId: string,
    applyToChildObjects: boolean,
  ): Observable<FeedRemovedItem[]> {
    const body = new URLSearchParams();
    body.set('selected_posts_feed_id', feedId);
    // * the legacy call omitted the field entirely when falsy — kept, because
    // * the endpoint treats "present" and "true" alike.
    if (applyToChildObjects) {
      body.set('apply_to_child_objects', 'true');
    }

    return this.http.post<FeedRemovedItem[]>(
      FEED_POST_REMOVE_SELECTED_ITEMS,
      body.toString(),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  /** Put one removed item back into the feed. */
  restoreRemovedItem(
    feedId: string,
    objectId: string,
    objectClassId: number,
  ): Observable<unknown> {
    const body = new URLSearchParams();
    body.set('object_id', objectId);
    body.set('object_class_id', objectClassId.toString());

    return this.http.delete(FEED_DELETE_REMOVED_ITEMS(feedId), {
      headers: HEADERS.formUrlEncodedNoCharset,
      body: body.toString(),
    });
  }

  /** Put every removed item back into the feed. */
  restoreAllRemovedItems(feedId: string): Observable<unknown> {
    return this.http.delete(FEED_DELETE_REMOVED_ITEMS(feedId));
  }
}
